import { ref } from 'vue'
import apiInstance from '@/utils/api'

export interface PriceLevel {
  id: string | number
  dataId: string | number
  label: string
  startTime: string
  endTime: string
  daysOfWeek: ('Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday')[]
}

export interface PriceLevelResponse {
  error: boolean
  message: string
  data: PriceLevel[] | PriceLevel
}

// Day name to ID mapping
const DAY_NAME_TO_ID: Record<string, number> = {
  'Saturday': 1,
  'Sunday': 2,
  'Monday': 3,
  'Tuesday': 4,
  'Wednesday': 5,
  'Thursday': 6,
  'Friday': 7,
}

// Day ID to name mapping
const DAY_ID_TO_NAME: Record<number, string> = {
  1: 'Saturday',
  2: 'Sunday',
  3: 'Monday',
  4: 'Tuesday',
  5: 'Wednesday',
  6: 'Thursday',
  7: 'Friday',
}

/**
 * Transform day names to day IDs for API request
 */
const transformDayNamesToIds = (dayNames: string[]): number[] => {
  return dayNames
    .map(name => DAY_NAME_TO_ID[name])
    .filter(id => id !== undefined)
}

/**
 * Transform day objects from API response to day names
 */
const transformDayIdsToNames = (days: Array<{ id: number; name: string }>): string[] => {
  return days
    .map(day => day.name)
    .filter((name): name is string => name !== undefined) as ('Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday')[]
}

/**
 * Transform API response to frontend format
 */
const transformApiResponseToFrontend = (apiData: any): PriceLevel => {
  return {
    id: apiData.id,
    dataId: apiData.data_id,
    label: apiData.name,
    startTime: apiData.start_time,
    endTime: apiData.end_time,
    daysOfWeek: transformDayIdsToNames(apiData.days),
  }
}

/**
 * Transform frontend format to API request format
 */
const transformFrontendToApiRequest = (data: Omit<PriceLevel, 'id' | 'dataId'>) => {
  return {
    name: data.label,
    start_time: data.startTime,
    end_time: data.endTime,
    days: transformDayNamesToIds(data.daysOfWeek),
  }
}

export const usePriceLevels = (getRestaurantId?: () => string) => {
  const levels = ref<PriceLevel[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Fetch price levels from backend
   */
  const fetchPriceLevels = async (): Promise<void> => {
    if (!getRestaurantId) {
      console.warn('Restaurant ID getter not provided')
      return
    }

    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      error.value = 'Restaurant ID is required to fetch price levels'
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await apiInstance.get<PriceLevelResponse>(
        `/restaurant/${restaurantId}/price-level`
      )

      if (response.data.error === false && Array.isArray(response.data.data)) {
        levels.value = response.data.data.map(transformApiResponseToFrontend)
      } else {
        error.value = response.data.message || 'Failed to fetch price levels'
        levels.value = []
      }
    } catch (err: any) {
      error.value = err?.response?.data?.message || err?.message || 'Failed to fetch price levels'
      levels.value = []
      console.error('Error fetching price levels:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Create a new price level
   */
  const createLevel = async (level: Omit<PriceLevel, 'id'>): Promise<PriceLevel | null> => {
    if (!getRestaurantId) {
      error.value = 'Restaurant ID getter not provided'
      return null
    }

    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      error.value = 'Restaurant ID is required to create price level'
      return null
    }

    isLoading.value = true
    error.value = null

    try {
      const payload = transformFrontendToApiRequest(level)
      const response = await apiInstance.post<PriceLevelResponse>(
        `/restaurant/${restaurantId}/price-level`,
        payload
      )

      if (response.data.error === false && response.data.data) {
        const newLevel = transformApiResponseToFrontend(response.data.data)
        levels.value.push(newLevel)
        return newLevel
      } else {
        error.value = response.data.message || 'Failed to create price level'
        return null
      }
    } catch (err: any) {
      error.value = err?.response?.data?.message || err?.message || 'Failed to create price level'
      console.error('Error creating price level:', err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Update an existing price level
   */
  const updateLevel = async (id: string | number, updates: Omit<PriceLevel, 'id'>): Promise<boolean> => {
    if (!getRestaurantId) {
      error.value = 'Restaurant ID getter not provided'
      return false
    }

    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      error.value = 'Restaurant ID is required to update price level'
      return false
    }

    isLoading.value = true
    error.value = null

    try {
      const payload = transformFrontendToApiRequest(updates)
      const response = await apiInstance.put<PriceLevelResponse>(
        `/restaurant/${restaurantId}/price-level/${id}`,
        payload
      )

      if (response.data.error === false && response.data.data) {
        const updatedLevel = transformApiResponseToFrontend(response.data.data)
        const index = levels.value.findIndex(l => l.id === id)
        if (index !== -1) {
          levels.value[index] = updatedLevel
        }
        return true
      } else {
        error.value = response.data.message || 'Failed to update price level'
        return false
      }
    } catch (err: any) {
      error.value = err?.response?.data?.message || err?.message || 'Failed to update price level'
      console.error('Error updating price level:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Delete a price level
   */
  const deleteLevel = async (id: string | number): Promise<boolean> => {
    if (!getRestaurantId) {
      error.value = 'Restaurant ID getter not provided'
      return false
    }

    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      error.value = 'Restaurant ID is required to delete price level'
      return false
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await apiInstance.delete(
        `/restaurant/${restaurantId}/price-level/${id}`
      )

      if (response.data.error === false) {
        levels.value = levels.value.filter(l => l.id !== id)
        return true
      } else {
        error.value = response.data.message || 'Failed to delete price level'
        return false
      }
    } catch (err: any) {
      error.value = err?.response?.data?.message || err?.message || 'Failed to delete price level'
      console.error('Error deleting price level:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Local methods for compatibility (backward compatibility)
   */
  const updateLevels = (newLevels: PriceLevel[]) => {
    levels.value = newLevels
  }

  const getLevels = () => {
    return levels.value
  }

  const reset = () => {
    levels.value = []
    isLoading.value = false
    error.value = null
  }

  return {
    // State
    levels,
    isLoading,
    error,

    // API Methods
    fetchPriceLevels,
    createLevel,
    updateLevel,
    deleteLevel,

    // Local Methods (backward compatibility)
    updateLevels,
    getLevels,
    reset,
  }
}
